import { useEffect, useState, useCallback } from 'react';
import { useAppSelector } from 'redux/store';
import {
  detectBrowserTimezone,
  areTimezonesEquivalent,
} from 'helpers/timezoneUtils';

type TTimezoneDetectionResult = {
  browserTimezone: string;
  userTimezone: string | undefined;
  hasMismatch: boolean;
  showPrompt: boolean;
  dismissPrompt: () => void;
};

const TIMEZONE_PROMPT_DISMISSED_KEY = 'timezone_prompt_dismissed';

/**
 * Hook to detect browser timezone and compare with user's saved timezone
 * Shows a prompt if there's a mismatch (unless previously dismissed)
 */
export const useTimezoneDetection = (): TTimezoneDetectionResult => {
  const user = useAppSelector(state => state.authSlice.user);
  const [showPrompt, setShowPrompt] = useState(false);
  const [browserTimezone] = useState(() => detectBrowserTimezone());

  // User's effective timezone (user preference → org timezone → undefined)
  const userTimezone = user?.timeZone || user?.organization?.timeZone;

  // Check if there's a timezone mismatch
  const hasMismatch =
    !!userTimezone &&
    !!browserTimezone &&
    !areTimezonesEquivalent(browserTimezone, userTimezone);

  // Check if prompt was previously dismissed for this session
  const wasPromptDismissed = useCallback(() => {
    try {
      const dismissed = sessionStorage.getItem(TIMEZONE_PROMPT_DISMISSED_KEY);
      return dismissed === 'true';
    } catch (error) {
      console.error('Failed to read from sessionStorage:', error);
      return false;
    }
  }, []);

  // Dismiss the prompt for this session
  const dismissPrompt = useCallback(() => {
    try {
      sessionStorage.setItem(TIMEZONE_PROMPT_DISMISSED_KEY, 'true');
    } catch (error) {
      console.error('Failed to write to sessionStorage:', error);
    }
    setShowPrompt(false);
  }, []);

  // Show prompt if there's a mismatch and it wasn't dismissed
  useEffect(() => {
    if (hasMismatch && !wasPromptDismissed()) {
      setShowPrompt(true);
    } else {
      setShowPrompt(false);
    }
  }, [hasMismatch, wasPromptDismissed]);

  return {
    browserTimezone,
    userTimezone,
    hasMismatch,
    showPrompt,
    dismissPrompt,
  };
};

export default useTimezoneDetection;
